import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

// ─── Notes Data (files from /downloads) ──────────────────────────────────────

const NOTES = [
  {
    title: "C Programming",
    filename: "C.7z",
    size: "22.4 KB",
    icon: "🔵",
    tags: ["Language", "Fundamentals"],
    description:
      "Complete C programming notes covering basics, pointers, memory management, and data structures.",
  },
  {
    title: "Hacking — All Chapters",
    filename: "hacking_all_chapters.7z",
    size: "22.6 KB",
    icon: "🟢",
    tags: ["Security", "Networking"],
    description:
      "Comprehensive ethical hacking notes covering all chapters from reconnaissance to exploitation.",
  },
  {
    title: "HTML",
    filename: "Html.7z",
    size: "2.4 KB",
    icon: "🟠",
    tags: ["Web", "Frontend"],
    description:
      "HTML fundamentals — elements, forms, semantic tags, and page structure.",
  },
  {
    title: "JavaScript",
    filename: "JavaScript.7z",
    size: "66.3 KB",
    icon: "🟡",
    tags: ["Language", "Web"],
    description:
      "JavaScript notes — ES6+, DOM manipulation, async/await, closures, and more.",
  },
  {
    title: "Next.js Backend Server",
    filename: "next js backend server.7z",
    size: "1.7 MB",
    icon: "⚫",
    tags: ["Framework", "Backend"],
    description:
      "Next.js server-side notes — API routes, SSR, middleware, and deployment.",
  },
  {
    title: "Node.js (Sigma)",
    filename: "node-js from sigma.7z",
    size: "659.5 KB",
    icon: "🟢",
    tags: ["Backend", "Runtime"],
    description:
      "Node.js notes — modules, Express, file system, streams, and REST APIs.",
  },
  {
    title: "Python",
    filename: "Python.7z",
    size: "13.9 KB",
    icon: "🔵",
    tags: ["Language", "Scripting"],
    description:
      "Python programming notes — syntax, OOP, libraries, and scripting techniques.",
  },
  {
    title: "React",
    filename: "react.7z",
    size: "22.7 MB",
    icon: "🔵",
    tags: ["Framework", "Frontend"],
    description:
      "React notes — components, hooks, state management, routing, and best practices.",
  },
  {
    title: "CSS Styling",
    filename: "Style CSS.7z",
    size: "6.2 KB",
    icon: "🟣",
    tags: ["Web", "Frontend"],
    description:
      "CSS notes — selectors, flexbox, grid, animations, and responsive design.",
  },
];

// ─── Colors ──────────────────────────────────────────────────────────────────

const C = {
  bg: "#0D1117",
  card: "#161B22",
  text: "#C9D1D9",
  muted: "#8B949E",
  accent: "#58A6FF",
  green: "#238636",
  greenHover: "#2EA043",
  border: "#30363D",
};

// ─── Inline Styles ───────────────────────────────────────────────────────────

const s = {
  page: {
    minHeight: "100vh",
    background: `linear-gradient(135deg, ${C.bg} 0%, ${C.card} 100%)`,
    color: C.text,
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    padding: "80px 20px 60px",
    position: "relative",
  },
  backBtn: {
    position: "fixed",
    top: 16,
    left: 16,
    zIndex: 1100,
    background: "rgba(255,255,255,0.08)",
    backdropFilter: "blur(12px)",
    border: `1px solid ${C.border}`,
    borderRadius: 50,
    padding: "10px 22px",
    color: C.accent,
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    gap: 8,
    transition: "all .3s ease",
  },
  heading: {
    textAlign: "center",
    fontSize: 32,
    fontWeight: 700,
    background: `linear-gradient(135deg, ${C.accent}, ${C.green})`,
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    marginBottom: 8,
  },
  subheading: {
    textAlign: "center",
    fontSize: 15,
    color: C.muted,
    marginBottom: 12,
  },

  /* Search */
  searchWrap: {
    maxWidth: 480,
    margin: "0 auto 36px",
    position: "relative",
  },
  searchInput: {
    width: "100%",
    padding: "12px 18px 12px 44px",
    background: "rgba(255,255,255,0.05)",
    border: `1px solid ${C.border}`,
    borderRadius: 50,
    color: C.text,
    fontSize: 15,
    outline: "none",
    transition: "border-color .3s",
    boxSizing: "border-box",
  },
  searchIcon: {
    position: "absolute",
    left: 16,
    top: "50%",
    transform: "translateY(-50%)",
    fontSize: 16,
    color: C.muted,
    pointerEvents: "none",
  },

  /* Grid */
  grid: {
    maxWidth: 1000,
    margin: "0 auto",
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(290px, 1fr))",
    gap: 22,
  },

  /* Card */
  card: {
    background: "rgba(22,27,34,0.65)",
    backdropFilter: "blur(20px)",
    borderRadius: 18,
    border: `1px solid ${C.border}`,
    padding: "24px 22px",
    transition: "transform .3s ease, box-shadow .3s ease",
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  cardHover: {
    transform: "translateY(-5px)",
    boxShadow: "0 14px 40px rgba(0,0,0,0.5)",
  },

  cardHeader: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  cardIcon: {
    fontSize: 32,
    lineHeight: 1,
  },
  cardTitle: {
    fontSize: 17,
    fontWeight: 700,
    color: C.text,
  },
  cardSize: {
    fontSize: 12,
    color: C.muted,
  },
  cardDesc: {
    fontSize: 13,
    color: C.muted,
    lineHeight: 1.55,
    flex: 1,
  },
  tags: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
  },
  tag: {
    padding: "4px 12px",
    borderRadius: 50,
    background: "rgba(88,166,255,0.1)",
    border: `1px solid rgba(88,166,255,0.2)`,
    color: C.accent,
    fontSize: 11,
    fontWeight: 600,
  },
  downloadBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    padding: "11px 20px",
    borderRadius: 50,
    border: "none",
    background: `linear-gradient(135deg, ${C.green}, ${C.greenHover})`,
    color: "#fff",
    fontWeight: 700,
    fontSize: 14,
    cursor: "pointer",
    transition: "all .3s ease",
    textDecoration: "none",
    marginTop: "auto",
  },

  /* Stats bar */
  stats: {
    maxWidth: 1000,
    margin: "0 auto 28px",
    display: "flex",
    justifyContent: "center",
    gap: 32,
    flexWrap: "wrap",
  },
  stat: {
    textAlign: "center",
  },
  statNum: {
    fontSize: 24,
    fontWeight: 700,
    color: C.accent,
  },
  statLabel: {
    fontSize: 12,
    color: C.muted,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },

  /* Empty */
  empty: {
    textAlign: "center",
    padding: "60px 20px",
    color: C.muted,
    gridColumn: "1 / -1",
  },
};

// ─── NoteCard Sub-component ──────────────────────────────────────────────────

function NoteCard({ note }) {
  const [hovered, setHovered] = useState(false);
  const downloadUrl = `${process.env.PUBLIC_URL}/downloads/${encodeURIComponent(note.filename)}`;

  return (
    <div
      style={{ ...s.card, ...(hovered ? s.cardHover : {}) }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div style={s.cardHeader}>
        <span style={s.cardIcon}>{note.icon}</span>
        <div>
          <div style={s.cardTitle}>{note.title}</div>
          <div style={s.cardSize}>{note.size} · .7z archive</div>
        </div>
      </div>

      <p style={s.cardDesc}>{note.description}</p>

      <div style={s.tags}>
        {note.tags.map((tag) => (
          <span key={tag} style={s.tag}>
            {tag}
          </span>
        ))}
      </div>

      <a
        href={downloadUrl}
        download
        style={s.downloadBtn}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "translateY(-2px)";
          e.currentTarget.style.boxShadow = "0 8px 22px rgba(35,134,54,0.35)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "none";
        }}
      >
        ⬇ Download Notes
      </a>
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function Notes() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");

  const filtered = NOTES.filter((n) => {
    const q = search.toLowerCase();
    return (
      n.title.toLowerCase().includes(q) ||
      n.description.toLowerCase().includes(q) ||
      n.tags.some((t) => t.toLowerCase().includes(q))
    );
  });

  return (
    <div style={s.page}>
      {/* Back button */}
      <button
        style={s.backBtn}
        onClick={() => navigate("/")}
        onMouseEnter={(e) =>
          (e.currentTarget.style.background = "rgba(88,166,255,0.15)")
        }
        onMouseLeave={(e) =>
          (e.currentTarget.style.background = "rgba(255,255,255,0.08)")
        }
      >
        ← Portfolio
      </button>

      {/* Heading */}
      <h1 style={s.heading}>📚 My Notes</h1>
      <p style={s.subheading}>
        Download my programming &amp; tech notes — free to use
      </p>

      {/* Stats */}
      <div style={s.stats}>
        <div style={s.stat}>
          <div style={s.statNum}>{NOTES.length}</div>
          <div style={s.statLabel}>Topics</div>
        </div>
        <div style={s.stat}>
          <div style={s.statNum}>9</div>
          <div style={s.statLabel}>Archives</div>
        </div>
        <div style={s.stat}>
          <div style={s.statNum}>.7z</div>
          <div style={s.statLabel}>Format</div>
        </div>
      </div>

      {/* Search */}
      <div style={s.searchWrap}>
        <span style={s.searchIcon}>🔍</span>
        <input
          style={s.searchInput}
          type="text"
          placeholder="Search notes by name, topic, or tag..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onFocus={(e) => (e.target.style.borderColor = C.accent)}
          onBlur={(e) => (e.target.style.borderColor = C.border)}
        />
      </div>

      {/* Grid */}
      <div style={s.grid}>
        {filtered.length === 0 ? (
          <div style={s.empty}>
            <div style={{ fontSize: 48, marginBottom: 12, opacity: 0.4 }}>
              🔍
            </div>
            <p style={{ fontSize: 16, fontWeight: 600 }}>No notes found</p>
            <p style={{ fontSize: 13 }}>Try a different search term</p>
          </div>
        ) : (
          filtered.map((note) => <NoteCard key={note.filename} note={note} />)
        )}
      </div>
    </div>
  );
}
